import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Sparkles, Trash2 } from "lucide-react";

const TONES = [
  { v: "warm", label: "Warm & human" },
  { v: "direct", label: "Direct & confident" },
  { v: "casual", label: "Casual (DM style)" },
];

const GOALS = [
  { v: "introduce", label: "Introduce myself" },
  { v: "gifted", label: "Ask for gifted trial" },
  { v: "collab", label: "Propose a collab" },
  { v: "paid", label: "Pitch a paid deal" },
];

const relative = (iso) => {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

export default function Outbound() {
  const [form, setForm] = useState({
    brand_name: "",
    brand_context: "",
    angle: "",
    tone: "warm",
    goal: "introduce",
  });
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState({ items: [], month_used: 0, month_limit: null, plan: "free" });

  const load = () => api.get("/outbound").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand_name.trim()) return toast.error("Add a brand name.");
    setBusy(true);
    setCurrent(null);
    try {
      const r = await api.post("/outbound/draft", form);
      setCurrent(r.data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Draft failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const remove = async (id) => {
    await api.delete(`/outbound/${id}`);
    load();
    if (current?.id === id) setCurrent(null);
  };

  const limitStr = history.month_limit == null ? "unlimited" : `${history.month_used}/${history.month_limit}`;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-3">
        // outbound pitcher
      </p>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-3">
        Pitch brands you actually want.
      </h1>
      <p className="text-neutral-400 mb-3 max-w-2xl">
        Don't wait for the inbox. Draft a personalized cold pitch to any brand — Claude tailors it to your
        media kit, so it doesn't sound like a template.
      </p>
      <p className="text-xs font-mono text-neutral-500 mb-10">
        Drafts this month: <span className="text-[#CCFF00]">{limitStr}</span>
        {history.plan === "free" && (
          <>
            {" · "}
            <Link to="/billing" className="text-[#CCFF00] hover:underline" data-testid="outbound-upgrade-link">
              Upgrade for more
            </Link>
          </>
        )}
      </p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="space-y-4" data-testid="outbound-form">
          <Field label="Brand name">
            <Input
              value={form.brand_name}
              onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
              placeholder="Nike"
              required
              className="h-12 bg-[#0e0e0e] border-white/10"
              data-testid="outbound-brand-input"
            />
          </Field>
          <Field label="What you know about them (product, launch, founder, ad you saw)">
            <Textarea
              rows={3}
              value={form.brand_context}
              onChange={(e) => setForm({ ...form, brand_context: e.target.value })}
              placeholder="Just dropped the Pegasus 41. Recently ran an ad with a running club in Boston."
              className="bg-[#0e0e0e] border-white/10"
              data-testid="outbound-context-textarea"
            />
          </Field>
          <Field label="Your angle (why you specifically)">
            <Textarea
              rows={3}
              value={form.angle}
              onChange={(e) => setForm({ ...form, angle: e.target.value })}
              placeholder="I run half-marathons and post training content. My audience is 60% women runners 25-40."
              className="bg-[#0e0e0e] border-white/10"
              data-testid="outbound-angle-textarea"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tone">
              <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                <SelectTrigger className="h-12 bg-[#0e0e0e] border-white/10" data-testid="outbound-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => (
                    <SelectItem key={t.v} value={t.v}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Goal">
              <Select value={form.goal} onValueChange={(v) => setForm({ ...form, goal: v })}>
                <SelectTrigger className="h-12 bg-[#0e0e0e] border-white/10" data-testid="outbound-goal-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOALS.map((g) => (
                    <SelectItem key={g.v} value={g.v}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
            data-testid="outbound-submit-btn"
          >
            {busy ? "Drafting…" : "Draft my pitch"} <Sparkles className="w-4 h-4" />
          </Button>
        </form>

        {/* Result + history */}
        <div className="space-y-4">
          {current && (
            <div className="p-6 rounded-2xl bg-[#141414] border border-[#CCFF00]/30" data-testid="outbound-current">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#CCFF00] mb-2">
                // your draft
              </p>
              <p className="font-display font-bold text-lg mb-1" data-testid="outbound-subject">
                {current.subject}
              </p>
              <p className="text-xs font-mono text-neutral-500 mb-4">
                To: {current.brand_name} · {current.tone} · {current.goal}
              </p>
              <div className="whitespace-pre-wrap text-neutral-200 text-sm leading-relaxed" data-testid="outbound-draft-text">
                {current.draft}
              </div>
              <Button
                onClick={() => copy(`Subject: ${current.subject}\n\n${current.draft}`)}
                variant="ghost"
                size="sm"
                className="mt-4 border border-white/15 rounded-full"
                data-testid="outbound-copy-btn"
              >
                <Copy className="w-3 h-3" /> Copy full pitch
              </Button>
            </div>
          )}

          {history.items.length > 0 && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
                // history · {history.items.length}
              </p>
              <div className="space-y-2" data-testid="outbound-history">
                {history.items.map((h) => (
                  <div
                    key={h.id}
                    className="p-4 rounded-xl bg-[#141414] border border-white/5 flex items-start justify-between gap-3"
                    data-testid={`outbound-history-item-${h.id}`}
                  >
                    <button
                      onClick={() => setCurrent(h)}
                      className="text-left min-w-0 flex-1"
                    >
                      <p className="font-display font-bold text-sm truncate">{h.brand_name}</p>
                      <p className="text-xs text-neutral-500 truncate">{h.subject}</p>
                      <p className="text-[10px] font-mono text-neutral-600 mt-1">
                        {h.tone} · {h.goal} · {relative(h.created_at)}
                      </p>
                    </button>
                    <button
                      onClick={() => remove(h.id)}
                      className="opacity-60 hover:opacity-100"
                      data-testid={`outbound-delete-${h.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-mono text-neutral-500 mb-1 block">{label}</span>
    {children}
  </label>
);
